import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCompanyAuth } from '../../contexts/CompanyAuthContext'
import { toast } from '../../components/toast/CustomToast'
import api from '../../utils/api'
import n4hLogo from '../../assets/logo.png'

// Stores list of companies ever logged into:
// [{ companyId, companyName, companyCnpj, isCore, associatedCount, coreId, coreName, email, password }]
const COMPANIES_KEY = 'company_saved_list'

const loadSavedCompanies = () => {
  try {
    const raw = localStorage.getItem(COMPANIES_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

const saveCompany = (entry) => {
  const list = loadSavedCompanies()
  const idx = list.findIndex(c => c.companyId === entry.companyId)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...entry }
  } else {
    list.unshift(entry)
  }
  localStorage.setItem(COMPANIES_KEY, JSON.stringify(list))
  return list
}

const CompanyLoginPage = () => {
  const navigate = useNavigate()
  const { login } = useCompanyAuth()

  const [savedCompanies, setSavedCompanies] = useState([])
  const [useNewId, setUseNewId] = useState(false)

  const [companyId, setCompanyId] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [companyName, setCompanyName] = useState('')
  const [companyCnpj, setCompanyCnpj] = useState('')
  const [companyLoading, setCompanyLoading] = useState(false)

  // Núcleo context: this company is a núcleo (has associated companies) and/or belongs to one
  const [isCore, setIsCore] = useState(false)
  const [associatedCount, setAssociatedCount] = useState(0)
  const [core, setCore] = useState(null) // { id, name }
  const activeCompanyIdRef = useRef('') // guards the silent refresh against out-of-order responses

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

  const coreRefName = (ref) => (ref ? ref.nome_fantasia || ref.razao_social || '' : '')

  const clearCoreInfo = () => {
    setIsCore(false)
    setAssociatedCount(0)
    setCore(null)
  }

  // data: { is_core, associated_companies_count, core } as returned by /validate and /login
  const applyCoreInfo = (data) => {
    setIsCore(!!data.is_core)
    setAssociatedCount(data.associated_companies_count || 0)
    setCore(data.core ? { id: data.core.id, name: coreRefName(data.core) } : null)
  }

  const fetchCompanyInfo = async (id) => {
    if (!id || !id.trim()) return
    setCompanyLoading(true)
    setCompanyName('')
    setCompanyCnpj('')
    clearCoreInfo()
    setError('')
    try {
      const response = await api.get(`/company-activation/validate/${id.trim()}`)
      setCompanyName(response.data.nome_fantasia || response.data.company_name || '')
      setCompanyCnpj(response.data.cnpj || '')
      applyCoreInfo(response.data)

      // Verify if company activation is complete
      try {
        const activationResponse = await api.get(`/company-activation/check-activation/${id.trim()}`)
        if (!activationResponse.data.is_activated) {
          setError('Esta empresa ainda não foi ativada. Por favor, complete o processo de ativação antes de fazer login.')
        }
      } catch (err) {
        setError('Esta empresa ainda não foi ativada. Por favor, complete o processo de ativação antes de fazer login.')
      }
    } catch {
      // silently ignore
    } finally {
      setCompanyLoading(false)
    }
  }

  // Re-validates a saved company in the background (no spinner, no activation check) so a stale
  // localStorage entry picks up núcleo changes. Keeps the cached data if the request fails.
  const refreshSavedCompany = async (id) => {
    try {
      const { data } = await api.get(`/company-activation/validate/${id}`)
      const name = data.nome_fantasia || data.company_name || ''
      const updated = saveCompany({
        companyId: id,
        companyName: name,
        companyCnpj: data.cnpj || '',
        isCore: !!data.is_core,
        associatedCount: data.associated_companies_count || 0,
        coreId: data.core?.id || null,
        coreName: coreRefName(data.core),
      })
      setSavedCompanies(updated)
      if (activeCompanyIdRef.current === id) {
        setCompanyName(name)
        setCompanyCnpj(data.cnpj || '')
        applyCoreInfo(data)
      }
    } catch {
      // keep cached data
    }
  }

  // Select a company from the saved list and populate fields
  const selectCompany = (company, { refresh = true } = {}) => {
    activeCompanyIdRef.current = company.companyId
    setCompanyId(company.companyId)
    setCompanyName(company.companyName || '')
    setCompanyCnpj(company.companyCnpj || '')
    setIsCore(!!company.isCore)
    setAssociatedCount(company.associatedCount || 0)
    setCore(company.coreId ? { id: company.coreId, name: company.coreName || '' } : null)
    if (refresh) refreshSavedCompany(company.companyId)
    if (company.email) {
      setEmail(company.email)
      setPassword(company.password || '')
      setRememberMe(true)
    } else {
      setEmail('')
      setPassword('')
      setRememberMe(false)
    }
  }

  // On mount: load saved list and pre-select first company
  useEffect(() => {
    const list = loadSavedCompanies()
    setSavedCompanies(list)
    if (list.length > 0) {
      // Cached entries render instantly and are refreshed silently; uncached ones are fetched with the loader
      selectCompany(list[0], { refresh: !!list[0].companyName })
      if (!list[0].companyName) fetchCompanyInfo(list[0].companyId)
    }
  }, [])

  // When user changes the select
  const handleSelectChange = (e) => {
    const val = e.target.value
    if (val === '__new__') {
      setUseNewId(true)
      setCompanyId('')
      setEmail('')
      setPassword('')
      setCompanyName('')
      setCompanyCnpj('')
      clearCoreInfo()
      activeCompanyIdRef.current = ''
      setRememberMe(false)
      return
    }
    const found = savedCompanies.find(c => c.companyId === val)
    if (found) selectCompany(found)
  }

  const handleLogin = async () => {
    setError('')

    if (!companyId.trim()) {
      setError('Por favor, informe o ID da empresa')
      return
    }
    if (!validateEmail(email)) {
      setError('Por favor, informe um email válido')
      return
    }
    if (!password.trim()) {
      setError('Por favor, informe sua senha')
      return
    }

    // Verify if company activation is complete before attempting login
    try {
      const activationResponse = await api.get(`/company-activation/check-activation/${companyId.trim()}`)
      if (!activationResponse.data.is_activated) {
        setError('Esta empresa ainda não foi ativada. Por favor, complete o processo de ativação antes de fazer login.')
        return
      }
    } catch (err) {
      setError('Esta empresa ainda não foi ativada. Por favor, complete o processo de ativação antes de fazer login.')
      return
    }

    setLoading(true)

    try {
      const response = await api.post('/company-activation/login', {
        email,
        password,
        company_id: companyId.trim()
      })

      const data = response.data

      localStorage.removeItem('access_token')
      localStorage.removeItem('company_id')
      localStorage.removeItem('user_info')
      localStorage.removeItem('company_integrations')

      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('company_id', data.company_id)
      localStorage.setItem('user_info', JSON.stringify({
        user_id: data.user_id,
        company_id: data.company_id,
        company_name: data.company_name,
        nome_fantasia: data.nome_fantasia,
        username: data.username,
        email: data.email,
        is_admin: data.is_admin,
        is_core: data.is_core,
        associated_companies_count: data.associated_companies_count,
        core: data.core
      }))
      localStorage.setItem('company_integrations', JSON.stringify(data.integrations || []))

      // Save company to list (always); credentials only if rememberMe
      const entry = {
        companyId: data.company_id,
        companyName: data.nome_fantasia || data.company_name || companyName,
        companyCnpj: companyCnpj,
        isCore: !!data.is_core,
        associatedCount: data.associated_companies_count || 0,
        coreId: data.core?.id || null,
        coreName: coreRefName(data.core),
        ...(rememberMe ? { email, password } : { email: '', password: '' })
      }
      const updated = saveCompany(entry)
      setSavedCompanies(updated)
      setUseNewId(false)

      login(data.access_token, data.company_id, {
        user_id: data.user_id,
        company_id: data.company_id,
        company_name: data.company_name,
        nome_fantasia: data.nome_fantasia,
        username: data.username,
        email: data.email,
        is_admin: data.is_admin,
        is_core: data.is_core,
        associated_companies_count: data.associated_companies_count,
        core: data.core
      })

      setCompanyName(data.nome_fantasia || data.company_name || companyName)
      applyCoreInfo(data)

      toast.success('Login realizado com sucesso!')

      setTimeout(() => {
        navigate('/dashboard')
      }, 1000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login. Tente novamente.')
      setLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) handleLogin()
  }

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '2px solid rgba(37,198,164,0.25)',
  }

  const showSelect = savedCompanies.length > 0 && !useNewId

  return (
    <div className="h-screen overflow-hidden flex items-center justify-center p-4 relative" style={{ backgroundColor: '#0A1C28' }}>
      {/* Background blobs */}
      <div className="absolute top-0 -left-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4' }} />
      <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full filter blur-3xl opacity-5 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '4s' }} />

      <div className="relative z-10 w-full max-w-4xl">
        <div
          className="flex rounded-3xl overflow-hidden backdrop-blur-2xl"
          style={{
            backgroundColor: 'rgba(10,28,40,0.85)',
            border: '1px solid rgba(37,198,164,0.2)',
            boxShadow: '0 0 60px rgba(37,198,164,0.08)',
            minHeight: '480px'
          }}
        >
          {/* Left — Branding + company info */}
          <div className="flex flex-col items-center justify-center gap-6 w-1/2 px-8 py-10">
            <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
              <img src={n4hLogo} alt="app" className="w-32 h-32 object-contain rounded-full" />
            </div>

            <div className="text-center">
              <h1 className="text-3xl tracking-wide" style={{ color: '#FFFFFF', fontFamily: 'Blare, sans-serif' }}>
                Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
              </h1>
              <h2 className="text-lg text-white mt-2" style={{ fontFamily: "'Stolzl', sans-serif" }}>
                Hub de Integrações
              </h2>
            </div>

            {companyLoading && (
              <div className="flex flex-col items-center gap-2">
                <span className="material-icons animate-spin" style={{ fontSize: 24, color: '#25C6A4' }}>sync</span>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Carregando dados da empresa...</span>
              </div>
            )}

            {!companyLoading && companyName && (
              <div className="flex flex-col items-center gap-3 w-full">
                <div className="w-12 h-px" style={{ backgroundColor: 'rgba(37,198,164,0.3)' }} />
                <div className="flex items-center gap-2">
                  <span className="material-icons" style={{ fontSize: 18, color: '#25C6A4' }}>domain</span>
                  <span className="text-white font-bold text-base text-center leading-snug">{companyName}</span>
                </div>
                {companyCnpj && (
                  <span
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: 'rgba(37,198,164,0.12)', border: '1px solid rgba(37,198,164,0.3)', color: '#25C6A4' }}
                  >
                    <span className="material-icons" style={{ fontSize: 12 }}>badge</span>
                    {companyCnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                  </span>
                )}

                {isCore && (
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide"
                      style={{ backgroundColor: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)', color: '#C4B5FD' }}
                    >
                      <span className="material-icons" style={{ fontSize: 14 }}>hub</span>
                      Núcleo
                    </span>
                    {associatedCount > 0 && (
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        {associatedCount} empresa{associatedCount !== 1 ? 's' : ''} associada{associatedCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}

                {!isCore && core && (
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-xl max-w-full"
                    style={{ backgroundColor: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}
                  >
                    <span className="material-icons flex-shrink-0" style={{ fontSize: 16, color: '#C4B5FD' }}>account_tree</span>
                    <span className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      Esta empresa faz parte do núcleo <strong style={{ color: '#FFFFFF' }}>{core.name}</strong>
                    </span>
                  </div>
                )}
              </div>
            )}

            {!companyLoading && !companyName && (
              <div className="text-center px-4">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Informe o ID da empresa para identificar sua organização.
                </p>
              </div>
            )}
          </div>

          {/* Vertical divider */}
          <div className="flex items-center py-10">
            <div className="w-px h-full" style={{ background: 'linear-gradient(to bottom, transparent, rgba(37,198,164,0.5) 30%, rgba(37,198,164,0.5) 70%, transparent)' }} />
          </div>

          {/* Right — Login form */}
          <div className="flex flex-col justify-center w-1/2 px-10 py-10">
            <h2 className="text-2xl font-bold text-white mb-6">Acessar Plataforma</h2>

            <div className="space-y-4 mb-5">

              {/* Company ID — select or text input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-white">ID da empresa</label>
                  {showSelect && (
                    <button
                      type="button"
                      onClick={() => { setUseNewId(true); setCompanyId(''); setCompanyName(''); setCompanyCnpj(''); clearCoreInfo(); activeCompanyIdRef.current = ''; setEmail(''); setPassword('') }}
                      className="text-xs flex items-center gap-1 transition-colors"
                      style={{ color: 'rgba(37,198,164,0.7)' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#25C6A4'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(37,198,164,0.7)'}
                    >
                      <span className="material-icons" style={{ fontSize: 13 }}>add</span>
                      Outro ID
                    </button>
                  )}
                  {useNewId && savedCompanies.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setUseNewId(false); selectCompany(savedCompanies[0]) }}
                      className="text-xs flex items-center gap-1 transition-colors"
                      style={{ color: 'rgba(37,198,164,0.7)' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#25C6A4'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(37,198,164,0.7)'}
                    >
                      <span className="material-icons" style={{ fontSize: 13 }}>arrow_back</span>
                      Empresas salvas
                    </button>
                  )}
                </div>

                {showSelect ? (
                  <div className="relative">
                    <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ fontSize: '18px', color: 'rgba(37,198,164,0.7)' }}>domain</span>
                    <select
                      value={companyId}
                      onChange={handleSelectChange}
                      disabled={loading}
                      className="w-full px-4 py-3 pl-10 rounded-xl focus:outline-none transition-all duration-200 text-white disabled:opacity-50 disabled:cursor-not-allowed appearance-none cursor-pointer"
                      style={{ ...inputStyle, colorScheme: 'dark' }}
                      onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                    >
                      {savedCompanies.map(c => (
                        <option key={c.companyId} value={c.companyId} style={{ backgroundColor: '#0A1C28' }}>
                          {c.companyName ? `${c.companyName}` : c.companyId}
                        </option>
                      ))}
                    </select>
                    <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ fontSize: '18px', color: 'rgba(255,255,255,0.3)' }}>expand_more</span>
                  </div>
                ) : (
                  <div className="relative">
                    <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: '18px', color: 'rgba(37,198,164,0.7)' }}>domain</span>
                    <input
                      type="text"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      placeholder="ID fornecido pelo administrador"
                      className="w-full px-4 py-3 pl-10 rounded-xl focus:outline-none transition-all duration-200 text-white placeholder:text-white/30 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                      onBlur={e => {
                        e.target.style.borderColor = 'rgba(37,198,164,0.25)'
                        fetchCompanyInfo(e.target.value)
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Email</label>
                <div className="relative">
                  <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: '18px', color: 'rgba(37,198,164,0.7)' }}>mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    placeholder="seu.email@empresa.com"
                    className="w-full px-4 py-3 pl-10 rounded-xl focus:outline-none transition-all duration-200 text-white placeholder:text-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Senha</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Sua senha"
                    disabled={loading}
                    className="w-full px-4 py-3 pr-10 rounded-xl focus:outline-none transition-all duration-200 text-white placeholder:text-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                  >
                    <span className="material-icons" style={{ fontSize: '18px' }}>
                      {showPassword ? 'visibility' : 'visibility_off'}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-3 mb-5">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded cursor-pointer"
                style={{ accentColor: '#25C6A4' }}
              />
              <label htmlFor="rememberMe" className="text-sm cursor-pointer select-none" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Lembrar-me neste dispositivo
              </label>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 rounded-lg flex items-start gap-2 mb-5" style={{ backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                <span className="material-icons text-red-400 flex-shrink-0" style={{ fontSize: '18px' }}>error</span>
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={handleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ background: 'linear-gradient(135deg, #25C6A4, #1aa085)', boxShadow: '0 4px 20px rgba(37,198,164,0.3)' }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 4px 28px rgba(37,198,164,0.5)' }}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,198,164,0.3)'}
            >
              {loading
                ? <span className="material-icons animate-spin" style={{ fontSize: '18px' }}>sync</span>
                : <span className="material-icons" style={{ fontSize: '18px' }}>login</span>
              }
              Acessar
            </button>

            <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.25)' }}>
              Powered by <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyLoginPage
